import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import api from "../lib/api";

function fmtErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ForgotPassword() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full border border-white/15 bg-ink-900/60 px-4 py-3 font-body text-white outline-none transition-colors focus:border-cyan";

  return (
    <div data-testid="forgot-page" className="relative flex min-h-screen items-center justify-center px-6 grid-lines">
      <div className="absolute left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 glow-blue" />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="relative w-full max-w-md border border-white/10 bg-ink-800/70 p-10 backdrop-blur-xl">
        {sent ? (
          <div data-testid="forgot-sent" className="text-center">
            <CheckCircle size={50} weight="thin" className="mx-auto text-cyan" />
            <h1 className="mt-5 font-head text-2xl font-black uppercase tracking-tight text-white">{t("reset_sent_title")}</h1>
            <p className="mt-4 font-body text-sm text-slate-300">{t("reset_sent_msg")}</p>
            <Link to="/login" className="mt-8 inline-block font-mono text-xs uppercase tracking-widest text-cyan hover:text-white">{t("back_to_login")}</Link>
          </div>
        ) : (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Wall Board Academy</p>
            <h1 className="mt-3 font-head text-3xl font-black uppercase tracking-tight text-white">{t("forgot_title")}</h1>
            <p className="mt-4 font-body text-sm text-slate-400">{t("forgot_sub")}</p>
            <form onSubmit={submit} className="mt-8 flex flex-col gap-5">
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{t("email")}</label>
                <input data-testid="forgot-email" type="email" required className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <button data-testid="forgot-submit" type="submit" disabled={loading} className="bg-cyan px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink-900 transition-colors hover:bg-white disabled:opacity-50">
                {loading ? "…" : t("send_reset_link")}
              </button>
            </form>
            <Link to="/login" className="mt-6 inline-block font-mono text-xs uppercase tracking-widest text-slate-500 hover:text-cyan">{t("back_to_login")}</Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
